import { Card } from '@mui/material';
import { useNavigate } from 'react-router';

import CheckPermissions from '@/components/CheckPermissions';
import DeviceStatusChip from '@/components/DeviceStatusChip';
import PERMISSIONS from '@/constants/auth.constants';
import { useStyles } from '@/features/DevicesTable';
import SourceStatusCountsChips from '@/features/DevicesTable/components/SourceStatusCountsChips/SourceStatusCountsChips';

import type { LimaSourceAggregatedStatusModel } from '../../SyncDevices/SyncDevices.types';
import type { ViewAggregatedStatusProps } from './ViewAggregatedStatus.types';

const EMPTY_RIGHT_BADGE_STYLE = { position: 'absolute', right: '0' } as const;
const HEADER_WRAPPER_STYLE = { margin: '0.75rem 0' } as const;
const HEADER_TITLE_STYLE = { width: '100%', margin: '0' } as const;
const HEADER_TEXT_STYLE = {
  marginTop: '.313rem',
  display: 'inline-flex'
} as const;

/**
 * Renders aggregated source status rows for a selected device.
 *
 * @param props Component properties.
 * @returns Aggregated source status section.
 */
const ViewAggregatedStatus = (props: ViewAggregatedStatusProps) => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { items } = props;

  const launchCloudSourceHandler = (
    source_type_id: number,
    device_uid: string
  ) => {
    navigate(
      `/devices/cloud-sources?q=${JSON.stringify({
        source_type_id: source_type_id.toString(),
        any: device_uid
      })}`
    );
  };

  /**
   * Renders one aggregated source-status card.
   *
   * @param aggRow Aggregated status row.
   * @returns Aggregated status card.
   */
  const renderAggregatedStatusCard = (
    aggRow: LimaSourceAggregatedStatusModel
  ) => (
    <Card
      key={`agg-status-${aggRow.source_type_id}`}
      className={classes.deviceDetailParentcard}
      onClick={() =>
        launchCloudSourceHandler(aggRow.source_type_id, aggRow.device_uid)
      }
    >
      <div className={classes.cloudSourceCard}>
        <div style={{ position: 'relative' }}>
          <span style={EMPTY_RIGHT_BADGE_STYLE} />
          <div className={classes.KVFlex}>
            <div className={classes.flexLeftCol}>Source</div>
            <div className={classes.flexRightCol}>{aggRow?.name}</div>
          </div>
          <div className={classes.KVFlex}>
            <div className={classes.flexLeftCol}>Status</div>
            <div className={classes.flexRightCol}>
              <DeviceStatusChip status={aggRow.status!} />
            </div>
          </div>
          <div className={classes.KVFlex}>
            <div className={classes.flexLeftCol}>Last Seen</div>
            <div className={classes.flexRightCol}>
              {aggRow.last_seen_display}
            </div>
          </div>
          <div className={classes.KVFlex}>
            <div className={classes.flexLeftCol}>Sources</div>
            <div className={classes.flexRightCol}>
              {aggRow.status !== undefined && (
                <CheckPermissions permissions={[PERMISSIONS.SOURCES_READ]}>
                  <SourceStatusCountsChips row={aggRow} />
                </CheckPermissions>
              )}
            </div>
          </div>
          <div className={classes.KVFlex} />
        </div>
      </div>
    </Card>
  );

  return (
    <>
      <div style={HEADER_WRAPPER_STYLE}>
        <h4 style={HEADER_TITLE_STYLE}>
          <CheckPermissions permissions={[PERMISSIONS.SOURCES_READ]}>
            <span style={HEADER_TEXT_STYLE}>AGGREGATED SOURCE STATUS</span>
          </CheckPermissions>
        </h4>
      </div>
      {items?.map(renderAggregatedStatusCard)}
    </>
  );
};

export default ViewAggregatedStatus;
